import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Checkbox,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useLocation, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../redux/store";
import useTabTitle from "../hooks/useTabTitle.hook";
import { getPermissions } from "../redux/slices/permissionsSlice";
import {
  PermissionsByDocKey,
  PermissionsItem,
} from "../interfaces/permissions-interface";
import {
  archiveUserRole,
  getUserRole,
  getUserRoleNames,
  updateUserRole,
} from "../redux/slices/userRoleSlice";
import { UpdateUserRole, UserRole } from "../interfaces/user-role.interface";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  inputStyle,
  pageHeading,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import AppContainer from "../components/AppContainer";
import { transformArrToObj } from "../utils/transform-permissions-obj";
import LoadingOverlay from "../components/LoadingOverlay";

const EditUserRolePage: React.FC = () => {
  useTabTitle();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { roleName: roleNameParam } = location.state;
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [currentUserPermissions, setCurrentUserPermissions] =
    useState<PermissionsByDocKey>();
  const { userRoleNames } = useSelector((state: RootState) => state.userRole);

  useDetectSession();

  const { permissions } = useSelector((state: RootState) => state.permissions);
  const { status: userRoleStatus, error: userRoleError } = useSelector(
    (state: RootState) => state.userRole
  );

  const [roleName, setRoleName] = useState<string>(roleNameParam);
  const [assignedUsers, setAssignedUsers] = useState<number>(0);
  const [selectedPermissions, setSelectedPermissions] = useState<{
    [key: string]: PermissionsItem;
  }>({});
  const [updateUserRolePayload, setUpdateUserRolePayload] =
    useState<UpdateUserRole>({
      oldRoleName: roleNameParam,
      newRoleName: undefined,
      rolePermissions: [],
      isPermissionsChanged: false,
      updatedBy: currentUser?.fullname ?? "",
    });

  const [edit, setEdit] = useState<boolean>(false);
  const [permissionsUpdated, setPermissionsUpdated] = useState<boolean>(false);
  const [showUpdatePermissionsPrompt, setShowUpdatePermissionsPrompt] =
    useState<boolean>(false);
  const [showUpdateRoleErr, setShowUpdateRoleErr] = useState<boolean>(false);
  const [updateRoleErr, setUpdateRoleErr] = useState<string>("");
  const [showDeletePrompt, setShowDeletePrompt] = useState<boolean>(false);

  const [status, setStatus] = useState<string>("loading");
  const [error, setError] = useState<string | null>(null);

  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  useEffect(() => {
    if (currentUser && currentUser.userPermissions) {
      setCurrentUserPermissions(transformArrToObj(currentUser.userPermissions));
    }
  }, [currentUser]);

  useEffect(() => {
    dispatch(getPermissions());
    dispatch(getUserRoleNames());

    setRoleName(roleNameParam);

    dispatch(getUserRole({ data: { roleName: roleNameParam } }))
      .unwrap()
      .then((item: UserRole) => {
        const permissionsByKey: any = {};
        item.rolePermissions?.forEach(
          (rolePermissionItem: PermissionsItem) =>
            (permissionsByKey[rolePermissionItem.permissionKey] = {
              ...rolePermissionItem,
            })
        );
        setSelectedPermissions(permissionsByKey);
        setUpdateUserRolePayload({
          oldRoleName: roleNameParam,
          newRoleName: undefined,
          rolePermissions: item.rolePermissions ?? [],
          isPermissionsChanged: false,
          updatedBy: currentUser?.fullname ?? "",
        });
        setAssignedUsers(item.numberOfUsers);
      });
  }, [currentUser?.fullname, dispatch, roleNameParam]);

  useEffect(() => {
    if (userRoleError) {
      setError(userRoleError);
      setSnackbarMessage(userRoleError);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
    if (userRoleStatus) {
      setStatus(userRoleStatus);
    }
  }, [userRoleError, userRoleStatus]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const buildUpdateUserRolePayload = (permissionItem: PermissionsItem) => {
    const isPreviouslyIncluded = updateUserRolePayload?.rolePermissions?.find(
      (item: PermissionsItem) =>
        item.permissionKey === permissionItem.permissionKey
    );
    if (!isPreviouslyIncluded) {
      setUpdateUserRolePayload({
        oldRoleName: roleNameParam,
        newRoleName: roleName,
        rolePermissions: [
          ...(updateUserRolePayload?.rolePermissions ?? []),
          permissionItem,
        ],
        isPermissionsChanged: true,
        updatedBy: currentUser?.fullname ?? "",
      });
    } else {
      setUpdateUserRolePayload({
        oldRoleName: roleNameParam,
        newRoleName: roleName,
        rolePermissions: updateUserRolePayload?.rolePermissions?.map(
          (item: PermissionsItem) => {
            if (item.permissionKey === permissionItem.permissionKey) {
              return permissionItem;
            }
            return item;
          }
        ),
        isPermissionsChanged: true,
        updatedBy: currentUser?.fullname ?? "",
      });
    }
    setPermissionsUpdated(true);
  };

  if (status === "failed") {
    return (
      <AppContainer>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <IconButton onClick={handleBackClick}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={pageHeading}>
          Edit User Role
        </Typography>
        {currentUserPermissions?.userRole?.permissions?.delete && (
          <IconButton
            onClick={() => {
              setShowDeletePrompt(true);
            }}
            style={{ position: "absolute", right: 100 }}
            disabled={assignedUsers > 0}
          >
            <DeleteOutlineIcon
              color={assignedUsers > 0 ? "disabled" : "error"}
            />
          </IconButton>
        )}
        {currentUserPermissions?.userRole?.permissions.edit && (
          <Button
            style={{ position: "absolute", right: 15 }}
            variant="contained"
            onClick={() => {
              setEdit(true);
            }}
            disabled={edit}
            sx={primaryBtnStyle}
          >
            Edit
          </Button>
        )}
      </Box>

      <LoadingOverlay loading={status === "loading"} />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Role Name */}
      <Box
        display="flex"
        alignItems="center"
        margin={2}
        flexDirection={"column"}
      >
        <Typography variant="h6" width={130}>
          Role Name
        </Typography>
        <TextField
          label={edit ? "Enter Role Name" : "Role Name"}
          value={roleName}
          onChange={(e) => {
            setRoleName(e.target?.value);
            setUpdateUserRolePayload((prev) => ({
              ...prev,
              newRoleName: e.target?.value,
            }));
          }}
          fullWidth
          margin="dense"
          disabled={!edit}
          sx={inputStyle()}
        />
      </Box>

      {/* Permissions Options */}
      <Box
        display="flex"
        alignItems="center"
        margin={2}
        flexDirection={"column"}
      >
        <Typography variant="h6" textAlign={"center"} margin={2}>
          Role Permissions
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            overflowX: "initial",
            "& th": {
              backgroundColor: "#E1E1E1",
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Module Name</TableCell>
                <TableCell>View</TableCell>
                <TableCell>Edit</TableCell>
                <TableCell>Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions?.map((item: PermissionsItem) => (
                <TableRow key={item?.moduleName}>
                  <TableCell>{item?.moduleName}</TableCell>
                  <TableCell>
                    {item?.permissions?.view && (
                      <Checkbox
                        checked={
                          selectedPermissions[item.permissionKey]?.permissions
                            ?.view ?? false
                        }
                        onClick={(_e) => {
                          const modifiedItem = {
                            permissionKey: item.permissionKey,
                            moduleName: item.moduleName,
                            permissions: {
                              view: !selectedPermissions[item.permissionKey]
                                ?.permissions?.view,
                              edit: selectedPermissions[item.permissionKey]
                                ?.permissions?.edit,
                              delete:
                                selectedPermissions[item.permissionKey]
                                  ?.permissions?.delete,
                            },
                          };
                          setSelectedPermissions({
                            ...selectedPermissions,
                            [item.permissionKey]: modifiedItem,
                          });
                          buildUpdateUserRolePayload(modifiedItem);
                        }}
                        disabled={!edit}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {item?.permissions?.edit && (
                      <Checkbox
                        checked={
                          selectedPermissions[item.permissionKey]?.permissions
                            ?.edit ?? false
                        }
                        onClick={(_e) => {
                          const modifiedItem = {
                            permissionKey: item.permissionKey,
                            moduleName: item.moduleName,
                            permissions: {
                              view: !selectedPermissions[item.permissionKey]
                                ?.permissions?.edit
                                ? true
                                : selectedPermissions[item.permissionKey]
                                    ?.permissions?.view,
                              edit: !selectedPermissions[item.permissionKey]
                                ?.permissions?.edit,
                              delete:
                                selectedPermissions[item.permissionKey]
                                  ?.permissions?.delete,
                            },
                          };
                          setSelectedPermissions({
                            ...selectedPermissions,
                            [item.permissionKey]: modifiedItem,
                          });
                          buildUpdateUserRolePayload(modifiedItem);
                        }}
                        disabled={!edit}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {item?.permissions?.delete && (
                      <Checkbox
                        checked={
                          selectedPermissions[item.permissionKey]?.permissions
                            ?.delete ?? false
                        }
                        onClick={(_e) => {
                          const modifiedItem = {
                            permissionKey: item.permissionKey,
                            moduleName: item.moduleName,
                            permissions: {
                              view: !selectedPermissions[item.permissionKey]
                                ?.permissions?.delete
                                ? true
                                : selectedPermissions[item.permissionKey]
                                    ?.permissions?.view,
                              edit: selectedPermissions[item.permissionKey]
                                ?.permissions?.edit,
                              delete:
                                !selectedPermissions[item.permissionKey]
                                  ?.permissions?.delete,
                            },
                          };
                          setSelectedPermissions({
                            ...selectedPermissions,
                            [item.permissionKey]: modifiedItem,
                          });
                          buildUpdateUserRolePayload(modifiedItem);
                        }}
                        disabled={!edit}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {edit && (
        <Box
          display="flex"
          margin={2}
          flexDirection={"row"}
          alignItems={"flex-end"}
          justifyContent={"flex-end"}
        >
          <Button
            variant="contained"
            disabled={
              !roleName ||
              !updateUserRolePayload.rolePermissions ||
              updateUserRolePayload.rolePermissions.every(
                (item: PermissionsItem) =>
                  !item.permissions.view &&
                  !item.permissions.edit &&
                  !item.permissions.delete
              )
            }
            onClick={() => {
              const matchedNames =
                userRoleNames?.filter((item: string) => item === roleName) ??
                [];
              if (
                matchedNames.length !== 0 &&
                !matchedNames?.every((item: string) => item === roleNameParam)
              ) {
                setUpdateRoleErr("Role Name already exists in database.");
                setShowUpdateRoleErr(true);
                return;
              }

              if (!permissionsUpdated) {
                dispatch(updateUserRole({ data: updateUserRolePayload }))
                  .unwrap()
                  .then(() => {
                    setEdit(false);
                    setRoleName(roleNameParam);
                    setPermissionsUpdated(false);
                    navigate("/manage-user-role");
                  })
                  .catch(() => {
                    setUpdateRoleErr("Failed to update user role.");
                    setShowUpdateRoleErr(true);
                  });
              } else {
                setShowUpdatePermissionsPrompt(true);
              }
            }}
            sx={primaryBtnStyle}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            style={{ marginLeft: 15 }}
            onClick={() => {
              dispatch(getUserRole({ data: { roleName: roleNameParam } }))
                .unwrap()
                .then((item: UserRole) => {
                  const permissionsByKey: any = {};
                  item.rolePermissions?.forEach(
                    (rolePermissionItem: PermissionsItem) =>
                      (permissionsByKey[rolePermissionItem.permissionKey] = {
                        ...rolePermissionItem,
                      })
                  );
                  setSelectedPermissions(permissionsByKey);
                  setEdit(false);
                  setRoleName(roleNameParam);
                });
            }}
            sx={primaryBtnStyleOutline}
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* Confirmation Modal */}
      <Dialog
        open={showUpdatePermissionsPrompt}
        onClose={() => {
          setShowUpdatePermissionsPrompt(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Update User Role Permissions
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Users with customized permissions for this role will have their
            settings reset to match the newly updated role permissions. Are you
            sure you want to save this change?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowUpdatePermissionsPrompt(false);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowUpdatePermissionsPrompt(false);
              dispatch(updateUserRole({ data: updateUserRolePayload }))
                .unwrap()
                .then(() => {
                  setEdit(false);
                  setRoleName(roleNameParam);
                  setPermissionsUpdated(false);
                  navigate("/manage-user-role");
                })
                .catch(() => {
                  setUpdateRoleErr("Failed to update user role");
                  setShowUpdateRoleErr(true);
                });
            }}
            color="primary"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={showDeletePrompt}
        onClose={() => {
          setShowDeletePrompt(false);
        }}
        aria-labelledby="Confirm Delete"
      >
        <DialogTitle>Delete {roleNameParam}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this role?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDeletePrompt(false);
            }}
            color="primary"
            sx={[primaryBtnStyleOutline, { width: "80px" }]}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              dispatch(
                archiveUserRole({
                  data: {
                    roleName: roleNameParam,
                    deletedBy: currentUser?.fullname ?? "",
                  },
                })
              )
                .unwrap()
                .then(() => {
                  setShowDeletePrompt(false);
                  navigate("/manage-user-role");
                })
                .catch(() => {
                  setSnackbarMessage("Failed to delete user role");
                  setSnackbarSeverity("error");
                  setOpenSnackbar(true);
                });
            }}
            autoFocus
            sx={[primaryBtnStyleOutline, { width: "180px" }]}
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showUpdateRoleErr}
        onClose={() => {
          setShowUpdateRoleErr(false);
          setUpdateRoleErr("");
        }}
      >
        <DialogTitle id="alert-dialog-title">
          Failed to Update User Role
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {updateRoleErr}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowUpdateRoleErr(false);
              setUpdateRoleErr("");
            }}
            color="primary"
            sx={[primaryBtnStyleOutline, { width: "60px" }]}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </AppContainer>
  );
};

export default EditUserRolePage;
